import { AfterViewInit, ChangeDetectorRef, Component, computed, ElementRef, inject, input, OnChanges, SimpleChanges, output } from '@angular/core';
import { IngredientChoice } from '../../models/menu/ingredient-choice.model';
import { ChoiceComponent } from "../choice/choice.component";
import { OrderableItem } from '../../models/menu/orderable-item.model';
import { OrderedChoice } from '../../models/ordering/ordered-choice.model';
import { OrderedItem } from '../../models/ordering/ordered-item.model';
import { ExpandableComponent } from "../../../shared/expandable-component/expandable.component";
import { CommonModule } from '@angular/common';
import { IconNameType } from '../../../shared/icons/icon-name-type.enum';
import { IconComponent } from "../../../shared/icons/icon.component";
import { ChoiceValidationResult } from '../../services/ordering/choice-validation-result.model';
import { OrderedChoiceService } from '../../services/ordering/ordered-choice.service';
import { DependableIngredientMappingService } from '../../services/dependable-ingr.service';

@Component({
  selector: 'cwo-choice-group',
  standalone: true,
  imports: [ChoiceComponent, ExpandableComponent, CommonModule, IconComponent],
  templateUrl: './choice-group.component.html',
  styleUrl: './choice-group.component.scss'
})
export class ChoiceGroupComponent implements AfterViewInit, OnChanges {
  choices = input.required<IngredientChoice[]>();
  name = input.required<string>();
  orderedChoices = input.required<OrderedChoice[]>();
  orderedItem = input.required<OrderedItem>();
  orderableItem = input.required<OrderableItem>();
  firstInvalidChoice = input('');
  isSpecial = input.required<boolean>();
  dependableChoices = input<Map<string, IngredientChoice[]>>(new Map<string, IngredientChoice[]>());
  toggleWarningsAfterSubmission = input(false);
  expandedInput = input<boolean>(false);
  iconNameType = IconNameType;

  orderedChoiceService = inject(OrderedChoiceService);
  private dependableIngredientMappingService = inject(DependableIngredientMappingService);

  private cd = inject(ChangeDetectorRef);
  expanded = false;
  scrollMarginTop?: number;
  choiceValidators?: ChoiceValidationResult[];

  readonly resetWarningsAfterSubmissionValue = output();

  readonly updateSpecialItem = output<OrderedItem>();

  choiceGroupExpandableId = computed(
    () => `${this.name()}-expandable-section`
  );

  nestedChoices = computed(() =>
    this.dependableIngredientMappingService.getSelectedDescendantChoices(
      (this.orderedChoices() ?? []).flatMap((orderedChoice) => (orderedChoice.ingredients ?? []).map(i => i.ingredient)),
      this.orderedItem(),
      this.dependableChoices()
    )
  );

    private elRef = inject(ElementRef);

    constructor() {}

    checkIfChoiceIsValid(controlExpansion = false) {
      this.choiceValidators = this.orderedChoices().map((choice) => {
        const orderableChoice = this.choices().find(
          (c) => c.name === choice.name
          ) as IngredientChoice;
        const choiceValidator = this.orderedChoiceService.validateChoice(
          choice,
          orderableChoice,
          this.orderedItem().size
        );
        return choiceValidator;
      }
      );
      if (this.choiceValidators) {
        for(const orderedChoice of this.orderedChoices()) {
          const choiceValidator = this.choiceValidators?.find(
            (choiceValidator) => choiceValidator.name === orderedChoice.name
          );
          if (choiceValidator) {
            orderedChoice.warnings = choiceValidator.valid
              ? []
              : [choiceValidator.longMessage];
          }
        }
  
      if(controlExpansion){
        this.expanded = this.choiceValidators.some((choiceValidator) => {
          const orderableChoice = this.orderableItem().choices.find(
            (c) => c.name === choiceValidator.name
          ) as IngredientChoice;
          if(orderableChoice.isOpenOnWeb || (choiceValidator && !choiceValidator.valid)){
            return true;
          }
          return false;
        });
      }
    }
  }

  ngAfterViewInit(): void {
    this.checkIfChoiceIsValid(true);
    if (this.expandedInput()) {
      this.expanded = this.expandedInput();
      this.cd.detectChanges();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
      if (
        changes['toggleWarningsAfterSubmission'] ||
        changes['firstInvalidChoice']
      ) {
        if (
          this.toggleWarningsAfterSubmission() &&
          this.choices().find(
            (choice) => choice.name === this.firstInvalidChoice()
          )
        ) {
          this.calcualteScrollMarginTop();
          this.elRef?.nativeElement.scrollIntoView({ behavior: 'smooth' });
        } else if (
          this.toggleWarningsAfterSubmission() &&
          this.firstInvalidChoice() &&
          this.nestedChoices().some((nestedChoice) => nestedChoice.name === this.firstInvalidChoice())
        ) {
          this.expanded = true;
        }
        
      }
      if(changes['orderedChoices'] && this.choices().find(
        (choice) => choice.ingredients.length > 0
      )) {
        this.checkIfChoiceIsValid();
      }
    }

  calcualteScrollMarginTop() {
    const informationContainerElRef = document.getElementById(
      'item-editor-item-information-container'
    );
    this.scrollMarginTop = informationContainerElRef?.getBoundingClientRect()
      .height
      ? informationContainerElRef?.getBoundingClientRect().height + 10
      : undefined;
  }

  handleExpansion(expanded: boolean) {
    this.calcualteScrollMarginTop();
    this.expanded = expanded;
    if (this.expanded) {
      const modalContent = document.getElementById(
        'item-editor-scrollable-wrapper'
      );
      const el = document.getElementById(this.choiceGroupExpandableId());
      const elContent = document.getElementById(
        this.choiceGroupExpandableId() + 'content'
      );
      const footer = document.getElementById('item-editor-footer');

      if (el && elContent && modalContent && footer) {
        const modalContentRect = modalContent.getBoundingClientRect();
        const elContentRect = elContent.getBoundingClientRect();
        const footerRect = footer.getBoundingClientRect();

        const visibleAreaHeight = modalContentRect.height - footerRect.height;

        if (elContentRect.bottom > visibleAreaHeight) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 10);
        }
      }
    }
  }

}
